import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';

import { ApiError, NetworkError } from '../api/client.js';
import type { StarterFeed } from '@feedreader/shared';
import type { Item, SubscriptionSummary } from '../api/types.js';
import { actionFor, shouldAdvance } from '../keyboard.js';
import { useSession } from '../session.js';
import {
  DEFAULT_PREFERENCES,
  loadPreferences,
  savePreferences,
  toggleCollapsed,
  type Preferences,
} from '../state/preferences.js';
import {
  TimelineStore,
  type TimelineFailure,
  type TimelineScope,
} from '../state/timeline.js';
import { ItemView } from './item-view.js';
import { Onboarding } from './onboarding.js';
import { AddFeed, InboxModeQuestion, ShortcutHelp } from './overlays.js';
import { Sidebar, type Folder } from './sidebar.js';
import { Empty, Failure, Loading } from './states.js';
import { TimelineList } from './timeline-list.js';

/**
 * The reader: sidebar, timeline, article (M1.15.1, M1.16, M1.17, M1.18).
 *
 * The composition root for everything a signed-in person sees, and the one
 * place that knows about all of it — which is why the logic it would be easy
 * to get wrong lives elsewhere: pagination and optimistic marks in
 * `TimelineStore`, the key map in `keyboard.ts`, the display default in
 * `preferences.ts`. What is left here is wiring, and wiring is best kept
 * boring.
 *
 * ## The keyboard listener is on the document, once
 *
 * A reader triages with their hands on `j` and `k` and never clicks anything,
 * so focus may be anywhere — or nowhere. One listener at the document, with
 * `keyboard.ts` deciding whether a key is ours, is the only arrangement where
 * that works; per-component handlers would require the right element to be
 * focused first.
 */

export function Reader(): ReactNode {
  const session = useSession();
  const { api, telemetry } = session;

  /* ---------------------------------------------------------- the store --- */

  const [counts, setCounts] = useState<{ total: number; per: Map<string, number> }>({
    total: 0,
    per: new Map(),
  });

  const store = useMemo(
    () =>
      new TimelineStore(api, {
        onCounts: (next) => {
          /*
           * Counts arriving from a mutation are authoritative and replace what
           * was held — never merged with a local tally (D2).
           */
          setCounts((current) => {
            const per = new Map(current.per);
            for (const [feedId, unread] of next.perSubscription) {
              per.set(feedId, unread);
            }
            return { total: next.total, per };
          });
        },
        onReachedEnd: (marked) => {
          telemetry.reachedEnd(marked);
        },
      }),
    [api, telemetry],
  );

  const snapshot = useSyncExternalStore(store.subscribe, store.getSnapshot);

  /* ------------------------------------------------------- what we know --- */

  const [subscriptions, setSubscriptions] = useState<readonly SubscriptionSummary[]>([]);
  const [folders, setFolders] = useState<readonly Folder[]>([]);
  const [booting, setBooting] = useState(true);
  const [preferences, setPreferences] = useState<Preferences>(DEFAULT_PREFERENCES);
  const [overlay, setOverlay] = useState<'none' | 'help' | 'add-feed' | 'onboarding'>(
    'none',
  );
  /**
   * The discovery track (M2.7.2), shown before anything else.
   *
   * Only for an account with **no subscriptions** that has not already made a
   * choice: somebody who has feeds does not need to be asked what to read, and
   * somebody who skipped should not be asked again on every load.
   */
  const [showStarter, setShowStarter] = useState(false);
  const [addError, setAddError] = useState<string | undefined>(undefined);
  /** Transient one-line feedback, for actions that are not here yet. */
  const [notice, setNotice] = useState<string | undefined>(undefined);
  const [adding, setAdding] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  /* ---- preferences, per account ---- */

  useEffect(() => {
    /*
     * Only once there is an account to key on. Loading under the placeholder
     * key would read somebody else's defaults and then ask the onboarding
     * question again a moment later.
     */
    if (session.status !== 'signed-in') {
      return;
    }

    const loaded = loadPreferences(session.userKey, window.localStorage);
    setPreferences(loaded);

    // Both directions: an account that has answered must not be asked again
    // because an earlier pass had no account to look up.
    /*
     * The Inbox-mode question comes *after* the starter set, so it is only
     * offered to an account that has been through onboarding — otherwise two
     * dialogs compete on a first load.
     */
    /*
     * Gated on `countsAsked` alone, not on `onboarded`. An account that
     * already has feeds — anybody who used this before the starter set
     * existed — has never been asked about counts and should be; the starter
     * screen has its own, separate gate.
     */
    setOverlay((current) => (loaded.countsAsked ? current : 'onboarding'));
  }, [session.userKey, session.status]);

  const updatePreferences = useCallback(
    (next: Preferences) => {
      setPreferences(next);
      savePreferences(session.userKey, next, window.localStorage);
    },
    [session.userKey],
  );

  /* ---- the first load ---- */

  const [bootFailure, setBootFailure] = useState<TimelineFailure | undefined>(undefined);

  const reload = useCallback(async () => {
    setBooting(true);
    setBootFailure(undefined);
    try {
      const [subs, folderList, serverCounts] = await Promise.all([
        api.subscriptions(),
        api.folders(),
        api.counts(),
      ]);

      setSubscriptions(subs);
      setFolders(folderList);

      /*
       * The empty-account case, which used to be an empty box and a URL field
       * (M2.7). Decided from what the server says rather than from a local
       * flag, so a second device does not offer it to somebody who already has
       * feeds — and `onboarded` keeps it from reappearing after a skip.
       */
      setShowStarter(
        subs.length === 0 &&
          !loadPreferences(session.userKey, window.localStorage).onboarded,
      );
      setCounts({
        total: serverCounts.total,
        per: new Map(
          serverCounts.subscriptions.map((entry) => [
            entry.subscriptionId,
            entry.unreadCount,
          ]),
        ),
      });

      await store.setScope({ filter: 'unread' });
    } catch (error) {
      /*
       * The first load failing is the common case at alpha — the app stack is
       * torn down and rebuilt, and the client survives it (D22). Caught here
       * rather than left to reject: an unhandled rejection is a blank screen
       * with an explanation only in the console.
       */
      setBootFailure(
        error instanceof NetworkError
          ? { kind: 'offline', message: 'The service could not be reached.' }
          : {
              kind: 'api',
              message:
                error instanceof ApiError ? error.userMessage : 'Something went wrong.',
            },
      );
    } finally {
      setBooting(false);
    }
  }, [api, store]);

  useEffect(() => {
    void reload();
  }, [reload]);

  /* ------------------------------------------------------- the keyboard --- */

  const selected = useMemo(
    () => snapshot.items.find((item) => item.id === snapshot.selectedId),
    [snapshot.items, snapshot.selectedId],
  );

  const toggleRead = useCallback(
    (item: Item, via: 'keyboard' | 'pointer') => {
      telemetry.action(item.read ? 'unread' : 'read', via);
      void store.setRead(item.id, !item.read);
    },
    [store, telemetry],
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      const action = actionFor(event, event.target);
      if (action === undefined) {
        return;
      }

      // Only now: an unhandled key must reach the browser untouched.
      event.preventDefault();

      switch (action) {
        case 'next':
          store.move(1);
          return;
        case 'previous':
          store.move(-1);
          return;
        case 'toggle-read':
          if (selected !== undefined) {
            toggleRead(selected, 'keyboard');
          }
          return;
        case 'star':
          /*
           * Stars are M4. The key is bound now so the overlay can list it and
           * so muscle memory is not trained on a key that later moves —
           * and pressing it says so rather than doing nothing, which reads as
           * a broken shortcut.
           */
          setNotice('Starring arrives with the archive (M4).');
          return;
        case 'mark-all-read':
          telemetry.action('mark_all_read', 'keyboard');
          void store.markAllRead();
          return;
        case 'open':
          if (selected?.link != null) {
            telemetry.action('open', 'keyboard');
            window.open(selected.link, '_blank', 'noopener,noreferrer');
          }
          return;
        case 'help':
          setOverlay((current) => (current === 'help' ? 'none' : 'help'));
          return;
        case 'close':
          setOverlay('none');
          return;
        case 'scroll-or-advance': {
          /*
           * `space` scrolls the article while there is more of it, and only
           * then advances. Advancing straight away would make anything longer
           * than the viewport unreadable.
           */
          const pane = scrollRef.current;
          if (pane === null || shouldAdvance(pane)) {
            store.move(1);
          } else {
            pane.scrollBy({ top: pane.clientHeight * 0.9 });
          }
          return;
        }
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [store, selected, toggleRead, telemetry]);

  /* ---- the transient notice clears itself ---- */

  useEffect(() => {
    if (notice === undefined) {
      return;
    }
    const timer = setTimeout(() => {
      setNotice(undefined);
    }, 4000);
    return () => {
      clearTimeout(timer);
    };
  }, [notice]);

  /* ---- H6: time to zero ---- */

  const triageReported = useRef(false);

  useEffect(() => {
    if (triageReported.current || counts.total !== 0 || snapshot.markedInSession === 0) {
      return;
    }
    triageReported.current = true;
    telemetry.triageComplete((Date.now() - telemetry.sessionStartedAt) / 1000);
  }, [counts.total, snapshot.markedInSession, telemetry]);

  /* ----------------------------------------------------------- rendering --- */

  /* ------------------------------------------------- M2.7.2 the starter set --- */

  const takeStarterFeeds = async (
    feeds: readonly StarterFeed[],
    categories: readonly string[],
  ): Promise<void> => {
    telemetry.onboardingTrack('starter');
    const started = Date.now();
    let added = 0;

    /*
     * Sequentially, not in parallel. Each subscribe fetches the feed and
     * ingests it inline (OI-1), and the per-domain rate limiter would refuse
     * a burst — five feeds from one publisher arriving at once would have
     * four of them fail for being polite.
     */
    for (const feed of feeds) {
      try {
        await api.subscribe(feed.url);
        added += 1;
      } catch {
        // One unreachable feed must not lose the other four. The report of
        // what did and did not work is M2.2.3's job, with import.
      }
    }

    telemetry.onboardingComplete(added, categories.length, (Date.now() - started) / 1000);

    updatePreferences({ ...preferences, onboarded: true });
    setShowStarter(false);
    // Then the second question, which is D18's (M1.18.1).
    setOverlay('onboarding');
    await reload();
  };

  /* ---------------------------------------- M2.5.1, M2.1.1 list management --- */

  const unsubscribe = async (feedId: string, title: string): Promise<void> => {
    /*
     * Confirmed, because it is irreversible from here and takes the read state
     * with it. Anything the user archived survives (D4), which is why the
     * wording says so rather than warning vaguely about data.
     */
    if (
      !window.confirm(
        `Unsubscribe from ${title}? Its read state goes too. Anything you have archived is kept.`,
      )
    ) {
      return;
    }

    try {
      await api.unsubscribe(feedId);
      if (snapshot.scope.feedId === feedId) {
        await store.setScope({ filter: snapshot.scope.filter });
      }
      await reload();
    } catch (error) {
      setNotice(
        error instanceof ApiError ? error.userMessage : 'That could not be removed.',
      );
    }
  };

  const createFolder = async (): Promise<void> => {
    const name = window.prompt('Name the folder');
    if (name === null || name.trim() === '') {
      return;
    }
    try {
      await api.createFolder(name.trim());
      await reload();
    } catch (error) {
      setNotice(error instanceof ApiError ? error.userMessage : 'That did not work.');
    }
  };

  const renameFolder = async (folderId: string, current: string): Promise<void> => {
    const name = window.prompt('Rename the folder', current);
    if (name === null || name.trim() === '' || name.trim() === current) {
      return;
    }
    try {
      await api.renameFolder(folderId, name.trim());
      await reload();
    } catch (error) {
      setNotice(error instanceof ApiError ? error.userMessage : 'That did not work.');
    }
  };

  const deleteFolder = async (folderId: string, name: string): Promise<void> => {
    // The feeds inside it are kept, which is the part worth saying out loud.
    if (!window.confirm(`Delete the folder ${name}? The feeds in it stay subscribed.`)) {
      return;
    }
    try {
      await api.deleteFolder(folderId);
      if (snapshot.scope.folderId === folderId) {
        await store.setScope({ filter: snapshot.scope.filter });
      }
      await reload();
    } catch (error) {
      setNotice(error instanceof ApiError ? error.userMessage : 'That did not work.');
    }
  };

  const addFeed = async (url: string): Promise<void> => {
    setAdding(true);
    setAddError(undefined);
    try {
      await api.subscribe(url);
      setOverlay('none');
      await reload();
    } catch (error) {
      setAddError(
        error instanceof ApiError ? error.userMessage : 'We could not reach the service.',
      );
    } finally {
      setAdding(false);
    }
  };

  if (showStarter && !booting) {
    return (
      <Onboarding
        onChoose={takeStarterFeeds}
        onAddOwn={() => {
          telemetry.onboardingTrack('own');
          updatePreferences({ ...preferences, onboarded: true });
          setShowStarter(false);
          setOverlay('add-feed');
        }}
        onSkip={() => {
          telemetry.onboardingTrack('skip');
          updatePreferences({ ...preferences, onboarded: true });
          setShowStarter(false);
          setOverlay('onboarding');
        }}
      />
    );
  }

  return (
    <div className="shell">
      <header className="shell__bar">
        <span className="shell__brand">Feed Reader</span>

        <div className="shell__filters">
          <button
            className={`chip${snapshot.scope.filter === 'unread' ? ' is-selected' : ''}`}
            onClick={() => {
              void store.setScope({ ...snapshot.scope, filter: 'unread' });
            }}
          >
            Unread
          </button>
          <button
            className={`chip${snapshot.scope.filter === 'all' ? ' is-selected' : ''}`}
            onClick={() => {
              void store.setScope({ ...snapshot.scope, filter: 'all' });
            }}
          >
            Everything
          </button>
        </div>

        {notice !== undefined && (
          <span className="shell__notice" role="status">
            {notice}
          </span>
        )}

        <div className="shell__right">
          <button
            className="link"
            onClick={() => {
              const next = { ...preferences, inboxMode: !preferences.inboxMode };
              updatePreferences(next);
              telemetry.inboxMode(next.inboxMode, 'settings');
            }}
            title="Show or hide unread counts"
          >
            {preferences.inboxMode ? 'Quiet mode' : 'Inbox mode'}
          </button>
          <button className="link" onClick={() => setOverlay('help')}>
            ?
          </button>
          <button className="link" onClick={session.signOut}>
            Sign out
          </button>
        </div>
      </header>

      <div className="shell__body">
        <Sidebar
          subscriptions={subscriptions}
          folders={folders}
          counts={counts.per}
          total={counts.total}
          scope={snapshot.scope}
          showCounts={preferences.inboxMode}
          collapsed={preferences.collapsedFolders}
          onSelect={(scope: TimelineScope) => {
            void store.setScope(scope);
          }}
          onToggleFolder={(folderId) => {
            updatePreferences(toggleCollapsed(preferences, folderId));
          }}
          onAddFeed={() => {
            setOverlay('add-feed');
          }}
          onUnsubscribe={(feedId, title) => {
            void unsubscribe(feedId, title);
          }}
          onCreateFolder={() => {
            void createFolder();
          }}
          onRenameFolder={(folderId, name) => {
            void renameFolder(folderId, name);
          }}
          onDeleteFolder={(folderId, name) => {
            void deleteFolder(folderId, name);
          }}
        />

        <section className="shell__timeline">
          {booting ? (
            <Loading label="Loading your feeds…" />
          ) : (bootFailure ??
              (snapshot.items.length === 0 ? snapshot.error : undefined)) !==
            undefined ? (
            <Failure
              failure={(bootFailure ?? snapshot.error) as TimelineFailure}
              onRetry={() => {
                void reload();
              }}
            />
          ) : snapshot.items.length === 0 && !snapshot.loading ? (
            <Empty
              reason={
                subscriptions.length === 0
                  ? 'no-subscriptions'
                  : snapshot.scope.filter === 'unread'
                    ? 'nothing-unread'
                    : 'nothing-here'
              }
              onAddFeed={() => {
                setOverlay('add-feed');
              }}
            />
          ) : (
            <TimelineList
              items={snapshot.items}
              selectedId={snapshot.selectedId}
              loading={snapshot.loading}
              hasMore={snapshot.hasMore}
              showCounts={preferences.inboxMode}
              onSelect={(item) => {
                store.select(item.id);
              }}
              onToggleRead={(item) => {
                toggleRead(item, 'pointer');
              }}
              onEndReached={() => {
                void store.loadMore();
              }}
              onScrolled={() => {
                telemetry.scrolled();
              }}
            />
          )}
        </section>

        <section className="shell__reader">
          <ItemView
            item={selected}
            scrollRef={scrollRef}
            onToggleRead={(read) => {
              if (selected !== undefined) {
                void store.setRead(selected.id, read);
              }
            }}
            onOpen={() => {
              telemetry.action('open', 'pointer');
            }}
          />
        </section>
      </div>

      {overlay === 'help' && (
        <ShortcutHelp
          onClose={() => {
            setOverlay('none');
          }}
        />
      )}

      {overlay === 'onboarding' && (
        <InboxModeQuestion
          onChoose={(inboxMode) => {
            updatePreferences({
              ...preferences,
              inboxMode,
              onboarded: true,
              countsAsked: true,
            });
            telemetry.inboxMode(inboxMode, 'onboarding');
            setOverlay('none');
          }}
        />
      )}

      {overlay === 'add-feed' && (
        <AddFeed
          busy={adding}
          error={addError}
          onSubmit={(url) => {
            void addFeed(url);
          }}
          onClose={() => {
            setOverlay('none');
          }}
        />
      )}
    </div>
  );
}
